import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { staffApi, fmtDate, fmtTime, fmtErr } from "../api";

export const StatusBadge = ({ value }) => {
  const v = (value || "").toLowerCase();
  const cls = v.includes("present")
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : v.includes("absent")
    ? "bg-red-50 text-[#C5221F] border-red-200"
    : v.includes("late") || v.includes("half")
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-slate-50 text-slate-600 border-slate-200";
  return <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>{value || "—"}</span>;
};

const Attendance = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await staffApi.get("/attendance/mine", { params: { limit: 90 } });
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        setError(fmtErr(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div data-testid="staff-attendance">
      <h1 className="font-display text-2xl font-bold text-slate-900">Attendance History</h1>
      <p className="mt-1 text-sm text-slate-500">Your recent attendance records. Check-in / check-out is done in the mobile app.</p>
      {loading ? (
        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : error ? (
        <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="attendance-error">{error}</p>
      ) : rows.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500 bg-white border border-slate-200 p-6" data-testid="attendance-empty">No attendance records found.</p>
      ) : (
        <div className="mt-6 bg-white border border-slate-200 overflow-x-auto" data-testid="attendance-table">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                {["Date", "Status", "Check-In", "Check-Out", "Hours", "Late By", "Branch"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`attendance-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{fmtDate(r.date)}</td>
                  <td className="px-4 py-3"><StatusBadge value={r.status} /></td>
                  <td className="px-4 py-3 text-slate-600">{fmtTime(r.check_in_time)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtTime(r.check_out_time)}</td>
                  <td className="px-4 py-3 text-slate-600">{r.total_hours != null ? `${r.total_hours} hrs` : "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.late_by_min ? `${r.late_by_min} min` : "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.branch_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Attendance;
